import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus';

// Constants for zoom default, limits, and increment
const ZOOM_SCALE_MAX = 2.5;
const ZOOM_SCALE_MIN = 0.5;
const ZOOM_SCALE_INCREMENT = 0.1;
const ZOOM_SCALE_DEFAULT = 1.0;

// Shared button styling
const BUTTON_STYLE = {
	bgcolor: '#1E2A35',
	color: '#FFFFFF',
	'&:hover': {
		bgcolor: '#2A3A47',
	},
};

/**
 * ScaleControl - A reusable component for zoom/scale controls
 * @param {number} scale - Current scale value
 * @param {function} onScaleChange - Callback function when scale changes
 * @param {boolean} isHorizontal - Layout direction: true for horizontal (-, reset, +), false for vertical (+, reset, -)
 * @param {object} sx - Additional styling for the container Box
 */
export default function ScaleControl({ scale, onScaleChange, isHorizontal = true, sx = {} }) {
	// Handler for zoom in action
	const handleZoomIn = () => {
		const newScale = Math.min(scale + ZOOM_SCALE_INCREMENT, ZOOM_SCALE_MAX);
		onScaleChange(newScale);
	};

	// Handler for zoom out action
	const handleZoomOut = () => {
		const newScale = Math.max(scale - ZOOM_SCALE_INCREMENT, ZOOM_SCALE_MIN);
		onScaleChange(newScale);
	};

	// Handler for resetting zoom to default
	const handleZoomReset = () => {
		onScaleChange(ZOOM_SCALE_DEFAULT);
	};

	// Determine flex direction based on isHorizontal prop
	const flexDirection = isHorizontal ? 'row' : 'column';

	// Button components
	const zoomInButton = (
		<IconButton  aria-label="Zoom in" onClick={handleZoomIn} size="small" sx={BUTTON_STYLE}>
			<AddIcon />
		</IconButton>
	);

	const resetButton = (
		<IconButton aria-label="Reset zoom" onClick={handleZoomReset} size="small" sx={BUTTON_STYLE}>
			<FilterCenterFocusIcon fontSize="small" />
		</IconButton>
	);

	const zoomOutButton = (
		<IconButton aria-label="Zoom out" onClick={handleZoomOut} size="small" sx={BUTTON_STYLE}>
			<RemoveIcon />
		</IconButton>
	);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: flexDirection,
				gap: '8px',
				...sx,
			}}
		>
			{isHorizontal ? (
				<>
					{zoomOutButton}
					{resetButton}
					{zoomInButton}
				</>
			) : (
				<>
					{zoomInButton}
					{resetButton}
					{zoomOutButton}
				</>
			)}
		</Box>
	);
}

export { ZOOM_SCALE_MAX, ZOOM_SCALE_MIN, ZOOM_SCALE_INCREMENT, ZOOM_SCALE_DEFAULT };
